import React, {useState, useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import {FaIcon} from '../../FaIcon';
import {LocalStorage} from '../../../utils/LocalStorage';
import {MCP_API_URL} from '../../../settings/local_settings';
import {SetupGuideCard, Step, CodeBlock, UrlBlock, OptionalPromptStep, OptionalSkillStep} from './SetupGuideCard';

function TokenDisplay() {
    const {t} = useTranslation();
    const [showToken, setShowToken] = useState(false);
    const [copied, setCopied] = useState(false);
    const token = LocalStorage.fetchUserToken();

    const handleCopy = useCallback(() => {
        navigator.clipboard.writeText(token).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    }, [token]);

    const displayToken = showToken ? token : token.substring(0, 8) + '...' + token.substring(token.length - 4);

    return (
        <div className="token-info">
            <span className="token-label">{t('dashbo_mcp.setup.your_token')}:</span>
            <span className="token-value">{displayToken}</span>
            <div className="token-actions">
                <button className="btn btn-sm btn-outline-secondary" onClick={() => setShowToken(!showToken)}>
                    {showToken ? t('dashbo_mcp.setup.hide_token') : t('dashbo_mcp.setup.show_token')}
                </button>
                <button className="btn btn-sm btn-outline-secondary" onClick={handleCopy}>
                    {copied ? t('dashbo_mcp.setup.copied') : t('dashbo_mcp.setup.copy_token')}
                </button>
            </div>
            <div className="token-warning">
                <FaIcon iconName="exclamation-triangle"/> {t('dashbo_mcp.setup.token_warning')}
            </div>
        </div>
    );
}

export function SetupGuidesSection({promptText}) {
    const {t} = useTranslation();
    const token = LocalStorage.fetchUserToken();
    const mcpUrl = MCP_API_URL;
    const apiUrl = `${mcpUrl}/api/v1`;
    const openapiUrl = `${mcpUrl}/openapi.json`;
    const sseUrl = `${mcpUrl}/sse`;
    const mcpHttpUrl = `${mcpUrl}/mcp`;

    return (
        <div className="dashbo-mcp-section">
            <h2 className="section-title">{t('dashbo_mcp.setup.title')}</h2>

            <TokenDisplay/>

            <div className="endpoints-row">
                <div className="endpoint-pill">
                    <span className="endpoint-label">{t('dashbo_mcp.setup.mcp_url')}</span>
                    <span className="endpoint-url">{mcpHttpUrl}</span>
                </div>
                <div className="endpoint-pill">
                    <span className="endpoint-label">{t('dashbo_mcp.setup.api_url')}</span>
                    <span className="endpoint-url">{apiUrl}</span>
                </div>
                <div className="endpoint-pill">
                    <span className="endpoint-label">{t('dashbo_mcp.setup.openapi_url')}</span>
                    <span className="endpoint-url">{openapiUrl}</span>
                </div>
            </div>

            <div className="setup-guides-grid">
                {/* ChatGPT */}
                <SetupGuideCard icon="robot" titleKey="dashbo_mcp.setup.chatgpt_title" descKey="dashbo_mcp.setup.chatgpt_desc"
                    noteKey="dashbo_mcp.setup.chatgpt_note">
                    <Step number={1}>{t('dashbo_mcp.setup.chatgpt_step1')}</Step>
                    <Step number={2}>{t('dashbo_mcp.setup.chatgpt_step2')}</Step>
                    <Step number={3}>{t('dashbo_mcp.setup.chatgpt_step3')}</Step>
                    <Step number={4}>
                        {t('dashbo_mcp.setup.chatgpt_step4')}
                        <CodeBlock>{openapiUrl}</CodeBlock>
                    </Step>
                    <Step number={5}>{t('dashbo_mcp.setup.chatgpt_step5')}</Step>
                    <Step number={6}>{t('dashbo_mcp.setup.chatgpt_step6')}</Step>
                    <OptionalPromptStep promptText={promptText}>
                        {t('dashbo_mcp.setup.optional_prompt_hint_chatgpt')}
                    </OptionalPromptStep>
                </SetupGuideCard>

                {/* Claude Code */}
                <SetupGuideCard icon="terminal" titleKey="dashbo_mcp.setup.claude_code_title" descKey="dashbo_mcp.setup.claude_code_desc">
                    <Step number={1}>
                        {t('dashbo_mcp.setup.claude_code_step1')}
                        <CodeBlock>{`claude mcp add -s user dashbo-connect --transport http ${mcpHttpUrl} -- --header "Authorization: Bearer ${token}"`}</CodeBlock>
                    </Step>
                    <Step number={2}>{t('dashbo_mcp.setup.claude_code_step2')}</Step>
                    <OptionalSkillStep/>
                </SetupGuideCard>

                {/* Claude Desktop */}
                <SetupGuideCard icon="microchip" titleKey="dashbo_mcp.setup.claude_title" descKey="dashbo_mcp.setup.claude_desc">
                    <Step number={1}>{t('dashbo_mcp.setup.claude_step1')}</Step>
                    <Step number={2}>{t('dashbo_mcp.setup.claude_step2')}</Step>
                    <Step number={3}>{t('dashbo_mcp.setup.claude_step3')}</Step>
                    <Step number={4}>
                        {t('dashbo_mcp.setup.claude_step4')}
                        <UrlBlock>{mcpHttpUrl}</UrlBlock>
                    </Step>
                    <Step number={5}>{t('dashbo_mcp.setup.claude_step5')}</Step>
                    <Step number={6}>{t('dashbo_mcp.setup.claude_step6')}</Step>
                    <OptionalPromptStep promptText={promptText}>
                        {t('dashbo_mcp.setup.optional_prompt_hint_claude_desktop')}
                    </OptionalPromptStep>
                </SetupGuideCard>

                {/* Claude Web */}
                <SetupGuideCard icon="microchip" titleKey="dashbo_mcp.setup.claude_web_title" descKey="dashbo_mcp.setup.claude_web_desc">
                    <Step number={1}>{t('dashbo_mcp.setup.claude_web_step1')}</Step>
                    <Step number={2}>{t('dashbo_mcp.setup.claude_web_step2')}</Step>
                    <Step number={3}>{t('dashbo_mcp.setup.claude_web_step3')}</Step>
                    <Step number={4}>
                        {t('dashbo_mcp.setup.claude_web_step4')}
                        <UrlBlock>{mcpHttpUrl}</UrlBlock>
                    </Step>
                    <Step number={5}>{t('dashbo_mcp.setup.claude_web_step5')}</Step>
                    <Step number={6}>{t('dashbo_mcp.setup.claude_web_step6')}</Step>
                    <OptionalPromptStep promptText={promptText}>
                        {t('dashbo_mcp.setup.optional_prompt_hint_claude_web')}
                    </OptionalPromptStep>
                </SetupGuideCard>

                {/* Make.com */}
                <SetupGuideCard icon="gear" titleKey="dashbo_mcp.setup.make_title" descKey="dashbo_mcp.setup.make_desc">
                    <Step number={1}>{t('dashbo_mcp.setup.make_step1')}</Step>
                    <Step number={2}>
                        {t('dashbo_mcp.setup.make_step2')}
                        <UrlBlock>{sseUrl}</UrlBlock>
                    </Step>
                    <Step number={3}>{t('dashbo_mcp.setup.make_step3')}</Step>
                </SetupGuideCard>

                {/* N8N */}
                <SetupGuideCard icon="shuffle" titleKey="dashbo_mcp.setup.n8n_title" descKey="dashbo_mcp.setup.n8n_desc">
                    <Step number={1}>{t('dashbo_mcp.setup.n8n_step1')}</Step>
                    <Step number={2}>
                        {t('dashbo_mcp.setup.n8n_step2')}
                        <UrlBlock>{apiUrl}/query</UrlBlock>
                    </Step>
                    <Step number={3}>{t('dashbo_mcp.setup.n8n_step3')}</Step>
                </SetupGuideCard>

                {/* Python / curl */}
                <SetupGuideCard icon="code" titleKey="dashbo_mcp.setup.code_title" descKey="dashbo_mcp.setup.code_desc">
                    <Step number={1}>
                        curl:
                        <CodeBlock>{`curl -X POST ${apiUrl}/query \\
  -H "Authorization: Bearer ${token}" \\
  -H "Content-Type: application/json" \\
  -d '{"client_id":"123","data_source_type":"AD_ACCOUNTS","date_range":{"startDate":"2024-01-01","endDate":"2024-01-31"},"fields":["Clicks","Costo"]}'`}</CodeBlock>
                    </Step>
                    <Step number={2}>
                        Python:
                        <CodeBlock>{`import requests

resp = requests.post(
    "${apiUrl}/query",
    headers={"Authorization": "Bearer ${token}"},
    json={
        "client_id": "123",
        "data_source_type": "AD_ACCOUNTS",
        "date_range": {"startDate": "2024-01-01", "endDate": "2024-01-31"},
        "fields": ["Clicks", "Costo"]
    }
)
print(resp.json())`}</CodeBlock>
                    </Step>
                </SetupGuideCard>
            </div>
        </div>
    );
}
